/**
 * TanStack Query hooks for permissions, groups and the people roster (permissions.manage admin).
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '../client'
import type { components } from '../generated/schema'

// ── Permissions & groups (permissions.manage admin) ─────────────────
// Server-side gate is `require_permission(PERMISSIONS_MANAGE)`
// (api/routers/permissions.py) on every route below. The client-side
// `permissions.manage` check that hides the Permissions tab (Settings.tsx)
// is presentation only, same relationship `is_owner` has to the routes it
// used to guard -- see MeOut's own docstring.

type GroupOut = components['schemas']['GroupOut']
type GroupCreateIn = components['schemas']['GroupCreateIn']
type GroupUpdateIn = components['schemas']['GroupUpdateIn']
type GroupPermissionsIn = components['schemas']['GroupPermissionsIn']

/** The permission catalog itself (S3-D5): keys live in code on the server, so
 *  a key Spec 3b adds appears here -- and therefore in the admin UI's
 *  checkboxes -- with no frontend change. */
export function usePermissionsCatalog() {
  return useQuery({
    queryKey: ['permissions-catalog'],
    queryFn: () => apiFetch<string[]>('/api/permissions'),
  })
}

export function useGroups() {
  return useQuery({
    queryKey: ['groups'],
    queryFn: () => apiFetch<GroupOut[]>('/api/groups'),
  })
}

export function useCreateGroup() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: GroupCreateIn) =>
      apiFetch<GroupOut>('/api/groups', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    // Appends the server's own response directly rather than
    // invalidate-and-refetch. PermissionsTab selects the new group the
    // instant this resolves (`onDone(g.id)`); an invalidate-only version
    // leaves a render (sometimes several, under real network latency) where
    // `groups` still lacks the new row and the newly-set `selectedId` can't
    // resolve to anything -- a visible "Select a group." flash at best, and
    // the trigger for a real AnimatePresence bug found in browser
    // verification at worst (see the `key={selected?.id ?? 'empty'}` comment
    // in PermissionsTab.tsx).
    onSuccess: (created) =>
      qc.setQueryData<GroupOut[]>(['groups'], (old) => (old ? [...old, created] : [created])),
  })
}

export function useUpdateGroup() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: GroupUpdateIn }) =>
      apiFetch<GroupOut>(`/api/groups/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    // Same reasoning as useCreateGroup: replace the one row directly from
    // the server's response rather than invalidate-and-refetch, so a rename
    // is reflected the instant the request resolves, with no gap where the
    // cache still holds the old name.
    onSuccess: (updated) =>
      qc.setQueryData<GroupOut[]>(['groups'], (old) => old?.map((g) => (g.id === updated.id ? updated : g))),
  })
}

export function useDeleteGroup() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => apiFetch<void>(`/api/groups/${id}`, { method: 'DELETE' }),
    // 204 -- nothing to merge in, so the row is removed by the id the
    // caller already has. Same invalidate-and-refetch gap as the other
    // group mutations otherwise (see useCreateGroup's comment).
    onSuccess: (_data, id) =>
      qc.setQueryData<GroupOut[]>(['groups'], (old) => old?.filter((g) => g.id !== id)),
  })
}

/** Full replace, not a toggle -- mirrors what the server does
 *  (`set_group_permissions` deletes every `group_permissions` row for the
 *  group, then inserts the given set). Callers must always send the
 *  complete desired set, not a delta. */
export function useSetGroupPermissions() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: GroupPermissionsIn }) =>
      apiFetch<GroupOut>(`/api/groups/${id}/permissions`, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    // Same reasoning as useCreateGroup/useUpdateGroup.
    onSuccess: (updated) =>
      qc.setQueryData<GroupOut[]>(['groups'], (old) => old?.map((g) => (g.id === updated.id ? updated : g))),
  })
}

// ── People roster (permissions.manage admin, Task 10) ────────────────
// `GET /api/users` is the source of truth `PUT /api/users/{id}/groups` was
// added to make safe -- that PUT is a full REPLACE, so the People panel must
// always compute the complete desired group set from what this roster last
// reported, never from just the one checkbox that was clicked.

type UserOut = components['schemas']['UserOut']
type UserGroupsIn = components['schemas']['UserGroupsIn']
type UserGroupsOut = components['schemas']['UserGroupsOut']

export function useUsers() {
  return useQuery({
    queryKey: ['users'],
    queryFn: () => apiFetch<UserOut[]>('/api/users'),
  })
}

/** Full replace, not a toggle -- mirrors `set_user_groups` server-side, which
 *  deletes every `user_groups` row for the user then inserts exactly what is
 *  sent. Refuses with 409 if the change would leave no user holding
 *  `permissions.manage` (same lockout guard as `useSetGroupPermissions`). */
export function useSetUserGroups() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: UserGroupsIn }) =>
      apiFetch<UserGroupsOut>(`/api/users/${id}/groups`, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    onSuccess: (result) => {
      // Merge the server's own returned membership back into the roster
      // directly, same reasoning as useCreateGroup: reflect the actual server
      // state the instant the request resolves, with no invalidate-and-refetch
      // gap where the checkbox briefly disagrees with what was just saved.
      qc.setQueryData<UserOut[]>(['users'], (old) =>
        old?.map((u) => (u.id === result.user_id ? { ...u, groups: result.groups } : u)),
      )
      // A membership change can move member_count on both the group the user
      // left and the one they joined -- invalidate rather than hand-patch,
      // since a single mutation response doesn't carry every affected
      // group's new count.
      qc.invalidateQueries({ queryKey: ['groups'] })
    },
  })
}
